import React, { createContext, useState, useCallback, useContext } from 'react';
import type { UserRole, AuthUser, AuthTenant } from '@/types/entities';
import { authApi } from '@/lib/api/auth';
import { collectionsApi } from '@/lib/api/collections';
import { formsApi } from '@/lib/api/forms';
import { extensionsApi } from '@/lib/api/extensions';
import { AUTH_REDIRECT_KEY, AUTH_TENANT_KEY, AUTH_TOKEN_KEY, AUTH_USER_KEY } from '@/config/constants';

type AuthContextType = {
  user: AuthUser | null;
  accessToken: string | null;

  isLoading: boolean;
  error: string | null;

  authenticate: (type: UserRole, email: string, password: string) => Promise<boolean>;
  authenticateWithPartnerToken: (tenantSlug: string, token: string) => Promise<boolean>;
  logout: () => Promise<boolean>;
};

type AuthState = {
  email: string | null;
  accessToken: string | null;
  tenant: AuthTenant | null;
  type: UserRole | null;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthContextProvider = ({ children }: React.PropsWithChildren) => {
  const [authState, setAuthState] = useState<AuthState>(getInitialAuthState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authenticate = useCallback(
    async (type: UserRole, email: string, password: string) => {
      try {
        setIsLoading(true);
        const apiCaller =
          type === "admin" ? authApi.adminSignIn : authApi.tenantSignIn;
        const authResponse = await apiCaller({ email: email, password: password });
        cacheAccessToken(authResponse.accessToken);

        let newTenant: AuthTenant | null = null;
        if (type === "tenant") {
          const tenantResponse = await authApi.getCurrentTenant();
          const [collectionsResponse, formsResponse, extensionsResponse] = await Promise.all([
            collectionsApi.getAllForTenant(tenantResponse.slug),
            formsApi.getAllForTenant(tenantResponse.slug),
            extensionsApi.getAllForTenant(tenantResponse.slug),
          ]);
          newTenant = {
            id: tenantResponse.id,
            name: tenantResponse.name,
            slug: tenantResponse.slug,
            websiteUrl: tenantResponse.website_url,
            collections: collectionsResponse,
            forms: formsResponse,
            extensions: extensionsResponse,
            createdAt: tenantResponse.created_at,
            updatedAt: tenantResponse.updated_at,
          };
        }

        setAuthState({
          email,
          type,
          accessToken: authResponse.accessToken,
          tenant: newTenant
        });

        cacheAuthentication(type, email, authResponse.accessToken, newTenant);
        return true;
      } catch (error: unknown) {
        setError(
          error instanceof Error
            ? error.message
            : "An error occurred: " + error
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const authenticateWithPartnerToken = useCallback(
    async (tenantSlug: string, token: string) => {
      try {
        setIsLoading(true);
        const authResponse = await authApi.partnerSignIn(tenantSlug, token);
        cacheAccessToken(authResponse.accessToken);

        const tenantResponse = await authApi.getCurrentTenant();
        const [collectionsResponse, formsResponse, extensionsResponse] = await Promise.all([
          collectionsApi.getAllForTenant(tenantResponse.slug),
          formsApi.getAllForTenant(tenantResponse.slug),
          extensionsApi.getAllForTenant(tenantResponse.slug),
        ]);

        const newTenant: AuthTenant = {
          id: tenantResponse.id,
          name: tenantResponse.name,
          slug: tenantResponse.slug,
          websiteUrl: tenantResponse.website_url,
          collections: collectionsResponse,
          forms: formsResponse,
          extensions: extensionsResponse,
          createdAt: tenantResponse.created_at,
          updatedAt: tenantResponse.updated_at,
        };

        const resolvedEmail = authResponse.email ?? '';

        setAuthState({
          email: resolvedEmail,
          type: 'tenant',
          accessToken: authResponse.accessToken,
          tenant: newTenant,
        });

        cacheAuthentication('tenant', resolvedEmail, authResponse.accessToken, newTenant);
        return true;
      } catch (error: unknown) {
        setError(
          error instanceof Error
            ? error.message
            : 'An error occurred: ' + error
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const logout = useCallback(async () => {
    clearAuthentication();
    setIsLoading(true);
    setAuthState({
      email: null,
      accessToken: null,
      tenant: null,
      type: null
    });
    setIsLoading(false);
    return true;
  }, [authState.type]);

  const value: AuthContextType = {
    user:
      authState.email == null || authState.type == null
        ? null
        : {
          email: authState.email,
          type: authState.type,
          tenant: authState.tenant
        },
    accessToken: authState.accessToken,
    isLoading: isLoading,
    error: error,
    authenticate: authenticate,
    authenticateWithPartnerToken,
    logout: logout
  };

  return (
    <AuthContext.Provider value={value}>
      {isLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.85)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div className="flex flex-col items-center">
            <svg
              className="animate-spin h-10 w-10 text-gray-600 mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            <span className="text-lg text-gray-700 font-semibold">Loading...</span>
          </div>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthContextProvider');
  }
  return context;
}

// Load initial authentication state from cache synchronously
const getInitialAuthState = (): AuthState => {
  const { cachedToken, cachedUser, cachedTenant } = getCachedAuthentication();
  if (!cachedToken || !cachedUser || isTokenExpired(cachedToken)) {
    clearAuthentication();
    return {
      email: null,
      accessToken: null,
      tenant: null,
      type: null
    };
  }

  try {
    const user = JSON.parse(cachedUser);
    let tenant: AuthTenant | null =
      user.type === "tenant" && cachedTenant
        ? JSON.parse(cachedTenant)
        : null;
    if (tenant && !Array.isArray(tenant.extensions)) {
      tenant = {
        ...tenant,
        extensions: [],
      };
    }
    return {
      email: user.email,
      type: user.type,
      accessToken: cachedToken,
      tenant: tenant
    };
  } catch {
    // Invalid cache, treat as logged out
    clearAuthentication();
    return {
      email: null,
      accessToken: null,
      tenant: null,
      type: null
    };
  }
};

const getCachedAuthentication = () => ({
  cachedToken: localStorage.getItem(AUTH_TOKEN_KEY),
  cachedUser: localStorage.getItem(AUTH_USER_KEY),
  cachedTenant: localStorage.getItem(AUTH_TENANT_KEY),
});
export const getNextAuthRedirect = () => localStorage.getItem(AUTH_REDIRECT_KEY);
export const setNextAuthRedirect = (redirectPath: string) => localStorage.setItem(AUTH_REDIRECT_KEY, redirectPath);
export const clearNextAuthRedirect = () => localStorage.removeItem(AUTH_REDIRECT_KEY);

const cacheAccessToken = (accessToken: string) => localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
const cacheAuthentication = (type: UserRole, email: string, accessToken: string, tenant: AuthTenant | null) => {
  localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify({ email: email, type: type }));
  if (type === "tenant" && tenant) {
    localStorage.setItem(AUTH_TENANT_KEY, JSON.stringify(tenant));
  }
};

const clearAuthentication = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_TENANT_KEY);
  localStorage.removeItem(AUTH_REDIRECT_KEY);
}

const isTokenExpired = (token: string): boolean => {
  try {
    // JWT structure: header.payload.signature (Base64Url encoded)
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const exp = payload.exp;
    if (!exp) return true;
    // exp is usually in seconds
    return Date.now() >= exp * 1000;
  } catch {
    // If we can't decode, treat as expired
    return true;
  }
};
